import IDrawerMenus from "../interfaces/IDrawerMenus";
import IDrawerSubMenus from "../interfaces/IDrawerSubMenus";

/**
 * GAMBIARRA TEMPORÁRIA — POC Localiza.
 *
 * O POC da Localiza foi desenvolvido na branch `localiza` e mergeado na
 * `develop`. No Modulare padrão todos os menus ficam visíveis e as telas do POC
 * moram agrupadas sob o menu "Localiza" (ver `mockData/modules.json`). Já para
 * os usuários da Localiza queremos reproduzir exatamente a visão que eles têm
 * hoje: apenas as telas do POC, no primeiro nível do menu (Dashboard,
 * Monitoramento e Relatórios).
 *
 * Em vez de duplicar a árvore de menus, promovemos os filhos do grupo
 * "Localiza" para o topo. O `modules.json` continua sendo a única fonte de
 * verdade — mexer nos itens do grupo reflete automaticamente nas duas visões.
 *
 * Quando o POC virar um módulo de verdade (com permissão por grupo/perfil vinda
 * da API), este arquivo e a chamada em `AppShell.tsx` podem ser removidos.
 */

/** ResourceKey do grupo que agrupa as telas do POC no `modules.json`. */
export const LOCALIZA_MENU_RESOURCE_KEY = "localiza";

/** Domínio usado para identificar os usuários da Localiza. */
const LOCALIZA_EMAIL_MARKER = "@localiza";

/** Rota inicial dos usuários da Localiza (primeira tela do POC). */
export const LOCALIZA_HOME_ROUTE = "/monitoring/dashboard";

/** Rota inicial dos demais usuários (Modulare padrão). */
export const DEFAULT_HOME_ROUTE = "/home";

/**
 * Identifica um usuário da Localiza pelo e-mail (ex.: `fulano@localiza.com`).
 */
export const isLocalizaUser = (email?: string | null): boolean =>
  (email ?? "").toLowerCase().includes(LOCALIZA_EMAIL_MARKER);

/** Rota inicial conforme o tipo de usuário. */
export const resolveHomeRoute = (email?: string | null): string =>
  isLocalizaUser(email) ? LOCALIZA_HOME_ROUTE : DEFAULT_HOME_ROUTE;

/** Converte um submenu em item de primeiro nível, preservando os filhos. */
const promoteToTopLevel = (sub: IDrawerSubMenus): IDrawerMenus => ({
  id: sub.id,
  label: sub.label,
  resourcekey: sub.resourceKey,
  icon: sub.icon,
  route: sub.route,
  isOpen: false,
  SubModules: sub.SubModules ?? [],
});

/**
 * Devolve o menu lateral que o usuário deve ver.
 *
 * - Usuário `@localiza`: só as telas do POC, promovidas do grupo "Localiza"
 *   para o primeiro nível.
 * - Qualquer outro usuário: o menu completo do Modulare, com as telas do POC
 *   agrupadas sob "Localiza".
 */
export const resolveVisibleMenus = (
  menus: readonly IDrawerMenus[],
  email?: string | null,
): IDrawerMenus[] => {
  if (!isLocalizaUser(email)) {
    return [...menus];
  }

  const localizaGroup = menus.find(
    (menu) => menu.resourcekey === LOCALIZA_MENU_RESOURCE_KEY,
  );

  // Sem o grupo (ou vazio) preferimos o menu completo a um menu em branco.
  if (!localizaGroup?.SubModules?.length) {
    return [...menus];
  }

  return localizaGroup.SubModules.map(promoteToTopLevel);
};
